import {
	Dispatch,
	SetStateAction,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	NotificationInterface,
	OrderInterface,
} from '../OrdersTable/interfaces/Order.interface';
import { OrderTarget } from '../OrdersTable/interfaces/OrderStatus.interface';
import Emitter, { EventType } from '../../services/events';
import { GeoService, toRouteDto } from '../../services/geo.services';
import { TruckRouteDto } from '../../services/truck.services';
import {
	TrackingLatLng,
	TrackingOrder,
	TrackingRouteMode,
} from '../../containers/Public/OrderTracking/orderTracking.interface';
import {
	mapPublicOrder,
	tripWindowOf,
} from '../../containers/Public/OrderTracking/orderTracking.mapper';
import {
	holdLastSeenPositions,
	isTruckInTransit,
	liveLegTarget,
	withLiveTrip,
	withoutLiveEta,
} from '../../containers/Public/OrderTracking/orderTracking.utils';
import {
	ApproachFetch,
	LiveLegTarget,
	fetchOrderRoute,
	fromLivePoint,
	historyToTelematics,
	isOlderFix,
	mergeTelematics,
	LivePosition,
	LivePositions,
	parseLivePosition,
	shouldRefetchApproach,
	streamingTruckIds,
	toTelematicsById,
	TruckTelematicsById,
	withAverageSpeed,
	withLivePositions,
	withLiveTrail,
	withOrderRoute,
	withTelematics,
} from './trackingOrder.utils';
import {
	FLEET_HISTORY_ROLES,
	TrackingService,
	TRACKING_ROLES,
} from '../../services/tracking.service';
import {
	LiveTrackingService,
	LiveViewerRole,
} from '../../services/liveTracking.services';
import {
	TRACKING_LIVE_FIX_MAX_AGE_MS,
	TRACKING_POLL_INTERVAL_MS,
} from '../../containers/Public/OrderTracking/orderTracking.constants';
import { UseTrackingOrderOptions } from './OrderRouteMap.interface';

/** How long a stored location waits for the first live point to replace it. */
const LIVE_FIRST_POINT_WAIT_MS = 5000;
// How often a silent stream is re-checked so its route and speed drop off.
const LIVE_STREAM_CHECK_MS = 10000;

const parseShape = (route?: TruckRouteDto): TrackingLatLng[] | undefined => {
	if (!route?.shape) return undefined;
	try {
		const points = JSON.parse(route.shape) as unknown;
		if (!Array.isArray(points)) return undefined;
		const path = points
			.filter(
				(point): point is [number, number] =>
					Array.isArray(point) &&
					Number.isFinite(point[0]) &&
					Number.isFinite(point[1]),
			)
			.map(([lat, lng]) => ({ lat, lng }));
		return path.length > 1 ? path : undefined;
	} catch (e) {
		return undefined;
	}
};

const putLivePosition = (
	setLivePositions: Dispatch<SetStateAction<LivePositions>>,
	live: { driverId: string; position: LivePosition } | undefined,
) => {
	if (!live) return;
	setLivePositions((prev) =>
		isOlderFix(prev[live.driverId], live.position)
			? prev
			: {
					...prev,
					[live.driverId]: withLiveTrail(
						prev[live.driverId],
						withAverageSpeed(prev[live.driverId], live.position),
					),
			  },
	);
};

const isLiveFix = (positionAt: string | undefined, now: number): boolean => {
	const at = positionAt ? Date.parse(positionAt) : NaN;
	return Number.isFinite(at) && now - at <= TRACKING_LIVE_FIX_MAX_AGE_MS;
};

// Which live legs this viewer may see: the producer only the way to the
// pickup, the customer only the way from it, admin and carrier both.
const legVisible = (mode: TrackingRouteMode, target: LiveLegTarget): boolean =>
	target === 'pickup' ? mode !== 'toDelivery' : mode !== 'toPickup';

interface LiveLeg {
	target: LiveLegTarget;
	path: TrackingLatLng[];
}

export const routeModeFor = (
	target: OrderTarget | string,
): TrackingRouteMode => {
	switch (target) {
		case OrderTarget.admin:
		case OrderTarget.transporter:
			return 'full';
		case OrderTarget.producer:
			return 'toPickup';
		default:
			return 'toDelivery';
	}
};

const useTrackingOrder = (
	order: OrderInterface | undefined,
	{ mode, showPendingTrucks = false }: UseTrackingOrderOptions,
): TrackingOrder | undefined => {
	const [route, setRoute] = useState<TruckRouteDto>();
	const [livePositions, setLivePositions] = useState<LivePositions>({});
	const [telematics, setTelematics] = useState<TruckTelematicsById>({});
	const [telematicsLoaded, setTelematicsLoaded] = useState<boolean>(false);
	const [routeSettled, setRouteSettled] = useState<boolean>(false);
	const [liveWaitOver, setLiveWaitOver] = useState<boolean>(false);
	const [liveLegs, setLiveLegs] = useState<Record<number, LiveLeg>>({});
	// When the gateway last said the driver app stopped streaming.
	const [liveOffAt, setLiveOffAt] = useState<number>();
	const [streamCheckedAt, setStreamCheckedAt] = useState<number>(Date.now());
	const approachCacheRef = useRef<Record<number, ApproachFetch>>({});
	const orderIdRef = useRef<number | undefined>(undefined);
	const mountedRef = useRef<boolean>(true);

	const orderId = order?.id;
	const target = order?.target;
	const pickupLocation = order?.pickupLocation;
	const deliveryLocation = order?.deliveryLocation;
	const pickupKey = pickupLocation?.coordinates?.join(',');
	const deliveryKey = deliveryLocation?.coordinates?.join(',');
	const showMainRoute = mode !== 'toPickup';

	useEffect(() => {
		orderIdRef.current = orderId;
		setRoute(undefined);
		setLivePositions({});
		setTelematics({});
		setTelematicsLoaded(false);
		setRouteSettled(false);
		setLiveWaitOver(false);
		setLiveLegs({});
		setLiveOffAt(undefined);
		approachCacheRef.current = {};
	}, [orderId]);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		if (!showMainRoute || !orderId || !pickupKey || !deliveryKey) return;
		let active = true;
		fetchOrderRoute(pickupLocation, deliveryLocation)
			.then((result) => {
				if (active) setRoute(result);
			})
			.catch((e) => console.error(e))
			.finally(() => {
				if (active) setRouteSettled(true);
			});
		return () => {
			active = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showMainRoute, orderId, pickupKey, deliveryKey]);

	useEffect(() => {
		if (!orderId) return;
		const onTruckLocation = (notify: NotificationInterface) => {
			if (!notify?.groupId || parseInt(notify.groupId, 10) !== orderId) {
				return;
			}
			putLivePosition(setLivePositions, parseLivePosition(notify.content));
		};
		Emitter.on(EventType.TRUCK_UPDATE_LOCATION, onTruckLocation);
		return () => {
			Emitter.off(EventType.TRUCK_UPDATE_LOCATION, onTruckLocation);
		};
	}, [orderId]);

	// Live watch: the gateway streams the order's drivers (fast GPS) only
	// while this map is open and the tab is visible.
	useEffect(() => {
		if (!orderId || !target || !TRACKING_ROLES.includes(target)) return;
		return LiveTrackingService.watchOrder(
			{ orderId, role: target as LiveViewerRole },
			{
				onPoint: (point) =>
					putLivePosition(setLivePositions, fromLivePoint(point)),
				onLiveMode: (on) => setLiveOffAt(on ? undefined : Date.now()),
				onError: () => setLiveWaitOver(true),
			},
		);
	}, [orderId, target]);

	const historyTrucks = useMemo(
		() =>
			(order?.trucks ?? [])
				.filter((truck) => truck.id !== undefined && !!truck.driver?.id)
				.map((truck) => ({
					id: truck.id,
					fleetId: truck.driver.id,
					...tripWindowOf(order!, truck),
				}))
				.filter((truck) => truck.from !== Infinity),
		[order],
	);
	const historyKey = historyTrucks
		.map((truck) => `${truck.id}:${truck.fleetId}:${truck.from}:${truck.to}`)
		.join('|');
	const historyTrucksRef = useRef(historyTrucks);
	historyTrucksRef.current = historyTrucks;

	// GPS history of the trucks, refreshed once a minute: the order's own
	// tracking answer (last fix, trail), plus each truck's breadcrumb trail
	// for the roles allowed to read it. The socket keeps the live position
	// current in between.
	useEffect(() => {
		if (!orderId || !target || !TRACKING_ROLES.includes(target)) return;
		let active = true;
		const loadHistory = () =>
			FLEET_HISTORY_ROLES.includes(target)
				? Promise.all(
						historyTrucksRef.current.map((truck) =>
							TrackingService.getFleetHistory(target, truck.fleetId, {
								from: truck.from,
								to: truck.to ?? Date.now(),
							})
								.then(
									({ data }) => [truck.id, historyToTelematics(data)] as const,
								)
								.catch(() => [truck.id, undefined] as const),
						),
				  )
				: Promise.resolve([]);
		const load = () =>
			Promise.all([
				TrackingService.getOrderTracking(target, orderId)
					.then(({ data }) => toTelematicsById(data?.trucks))
					.catch(() => ({} as TruckTelematicsById)),
				loadHistory(),
			])
				.then(([tracked, history]) => {
					if (!active) return;
					const merged = { ...tracked };
					history.forEach(([id, telematics]) => {
						const combined = mergeTelematics(merged[id], telematics);
						if (combined) merged[id] = combined;
					});
					setTelematics(merged);
				})
				.finally(() => {
					if (active) setTelematicsLoaded(true);
				});
		load();
		const timer = setInterval(load, TRACKING_POLL_INTERVAL_MS);
		return () => {
			active = false;
			clearInterval(timer);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderId, target, historyKey]);

	// A stream that went quiet is noticed even though no new point arrives.
	useEffect(() => {
		if (!Object.keys(livePositions).length) return;
		const timer = setInterval(
			() => setStreamCheckedAt(Date.now()),
			LIVE_STREAM_CHECK_MS,
		);
		return () => clearInterval(timer);
	}, [livePositions]);

	// Only trucks the driver app streams right now get a route ahead,
	// speed and ETA; the others sit at their last fix with a "last seen".
	const streaming = useMemo(
		() =>
			streamingTruckIds(
				order?.trucks,
				livePositions,
				liveOffAt,
				streamCheckedAt,
			),
		[order?.trucks, livePositions, liveOffAt, streamCheckedAt],
	);

	// Give the socket a moment for the first point once /tracking answered.
	useEffect(() => {
		if (!telematicsLoaded || liveWaitOver) return;
		const timer = setTimeout(
			() => setLiveWaitOver(true),
			LIVE_FIRST_POINT_WAIT_MS,
		);
		return () => clearTimeout(timer);
	}, [telematicsLoaded, liveWaitOver]);

	const tracksPositions = !!target && TRACKING_ROLES.includes(target);
	const positionsPending =
		tracksPositions &&
		!(
			telematicsLoaded &&
			(liveWaitOver ||
				Object.keys(livePositions).length > 0 ||
				Object.values(telematics).some((item) => !!item.position))
		);

	const canFetchRoute =
		showMainRoute && !!orderId && !!pickupKey && !!deliveryKey;

	const baseTracking = useMemo(() => {
		if (!order) return undefined;
		const mapped = mapPublicOrder(
			withLivePositions(
				withTelematics(withOrderRoute(order, route), telematics),
				livePositions,
			),
			{
				showPendingWithFreshPosition: showPendingTrucks,
				fallbackRoute: route,
				routeMode: mode,
			},
		);
		return {
			...mapped,
			routePending: canFetchRoute && !routeSettled && !mapped.routeKnown,
			positionsPending,
		};
	}, [
		order,
		route,
		telematics,
		livePositions,
		showPendingTrucks,
		mode,
		canFetchRoute,
		routeSettled,
		positionsPending,
	]);

	// Keeps a truck at its last GPS fix while the driver app is offline.
	const heldRef = useRef<TrackingOrder>();
	const heldTracking = useMemo(() => {
		if (!baseTracking) return baseTracking;
		heldRef.current = holdLastSeenPositions(heldRef.current, baseTracking);
		return heldRef.current;
	}, [baseTracking]);

	// The road ahead of each truck from where it is right now: to the pickup
	// until loaded, then to the delivery. Routed once a minute at most.
	useEffect(() => {
		if (!heldTracking || heldTracking.positionsPending) return;
		const requestedFor = orderIdRef.current;

		heldTracking.trucks.forEach((truck) => {
			const target = liveLegTarget(truck);
			if (!target || !legVisible(mode, target) || !streaming.has(truck.id)) {
				return;
			}
			const destination =
				target === 'pickup'
					? heldTracking.pickup.position
					: heldTracking.delivery.position;
			const position = truck.position!;
			const now = Date.now();
			const live = isLiveFix(truck.positionAt, now);
			if (
				!shouldRefetchApproach(
					approachCacheRef.current[truck.id],
					position,
					now,
					live,
					target,
				)
			) {
				return;
			}
			approachCacheRef.current[truck.id] = { position, at: now, live, target };

			GeoService.getRoute({
				points: [
					`${position.lat},${position.lng}`,
					`${destination.lat},${destination.lng}`,
				],
			})
				.then((response) => parseShape(toRouteDto(response)))
				.catch(() => undefined)
				.then((shape) => {
					if (!mountedRef.current || orderIdRef.current !== requestedFor) {
						return;
					}
					setLiveLegs((prev) => ({
						...prev,
						[truck.id]: { target, path: shape ?? [position, destination] },
					}));
				});
		});
	}, [mode, heldTracking, streaming]);

	return useMemo(() => {
		if (!heldTracking) return heldTracking;
		return {
			...heldTracking,
			trucks: heldTracking.trucks.map((truck) => {
				if (!streaming.has(truck.id)) {
					return {
						...withoutLiveEta(truck),
						speedKmh: 0,
						stoppedSince: undefined,
						liveSpeedKmh: undefined,
						signalLost: !!truck.position && isTruckInTransit(truck),
					};
				}
				const target = liveLegTarget(truck);
				const leg = liveLegs[truck.id];
				const live = { ...truck, signalLost: false };

				const routed =
					!target || !legVisible(mode, target) || leg?.target !== target
						? live
						: target === 'pickup'
						? { ...live, approachRoute: leg.path }
						: { ...live, deliveryRoute: leg.path };

				return withLiveTrip(routed, heldTracking, mode);
			}),
		};
	}, [heldTracking, mode, liveLegs, streaming]);
};

export default useTrackingOrder;
